import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Heart, Car, Home, Umbrella, Users, CheckCircle, ArrowRight, Phone, FileText, Clock, Award } from 'lucide-react';

const InsurancePage = () => {
  const insuranceTypes = [
    {
      icon: Heart,
      title: "Life Insurance",
      description: "Protect your family's financial future with comprehensive life coverage",
      features: ["Term Insurance", "Endowment Plans", "ULIPs", "Pension Plans"],
      color: "from-red-500 to-pink-600"
    },
    {
      icon: Shield,
      title: "Health Insurance",
      description: "Cover medical expenses for you and your family",
      features: ["Individual Plans", "Family Floater", "Critical Illness", "Top-Up Plans"],
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: Car,
      title: "Motor Insurance",
      description: "Comprehensive protection for your vehicles",
      features: ["Car Insurance", "Two-Wheeler", "Commercial Vehicle", "Third Party"],
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: Home,
      title: "Home Insurance",
      description: "Safeguard your home and belongings",
      features: ["Structure Cover", "Contents Cover", "Natural Disasters", "Theft Protection"],
      color: "from-orange-500 to-amber-600"
    }
  ];

  const benefits = [
    { icon: Shield, title: "Financial Security", desc: "Protect your loved ones from financial hardships" },
    { icon: FileText, title: "Tax Benefits", desc: "Save taxes under Section 80C and 80D" },
    { icon: Clock, title: "Peace of Mind", desc: "Stay worry-free knowing you're covered" },
    { icon: Award, title: "Wealth Creation", desc: "Build corpus while staying protected" },
    { icon: Users, title: "Family Protection", desc: "Secure your family's dreams and goals" },
    { icon: Umbrella, title: "Risk Mitigation", desc: "Transfer financial risks to insurers" }
  ];

  const process = [
    { step: "1", title: "Compare Plans", desc: "Browse and compare policies from top insurers" },
    { step: "2", title: "Get Quote", desc: "Receive instant premium quotes online" },
    { step: "3", title: "Fill Details", desc: "Complete the application with basic information" },
    { step: "4", title: "Get Covered", desc: "Receive policy document instantly" }
  ];

  const partners = [
    "HDFC Life", "ICICI Prudential", "Max Life", "SBI Life", 
    "Bajaj Allianz", "Star Health", "Care Insurance", "Tata AIG"
  ];

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10" />
        <div className="absolute top-20 right-20 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-10 left-10 w-60 h-60 bg-pink-500/15 rounded-full blur-3xl" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-400/10 border border-purple-400/30 rounded-full mb-6">
              <Shield className="w-4 h-4 text-purple-400" />
              <span className="text-purple-400 text-sm font-medium">Comprehensive Protection</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Insurance for
              <span className="block text-yellow-400">Every Need</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Protect what matters most. Compare and buy insurance plans from India's 
              top insurers at the best prices.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
              >
                Get Free Quote <ArrowRight className="inline ml-2 w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-8 py-4 bg-white/10 text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all flex items-center justify-center gap-2"
              >
                <Phone className="w-5 h-5" /> Talk to Expert
              </motion.button>
            </div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16"
          >
            {[
              { value: "50+", label: "Insurance Partners" },
              { value: "10L+", label: "Policies Sold" },
              { value: "₹500Cr+", label: "Claims Settled" },
              { value: "4.8★", label: "Customer Rating" }
            ].map((stat, index) => (
              <div key={index} className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                <div className="text-2xl md:text-3xl font-bold text-yellow-400">{stat.value}</div>
                <div className="text-gray-400 text-sm mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Insurance Types */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Types of Insurance
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Comprehensive coverage options to protect every aspect of your life
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {insuranceTypes.map((insurance, index) => (
              <motion.div
                key={insurance.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 hover:border-yellow-500/50 transition-all cursor-pointer group"
              >
                <div className="flex items-start gap-6">
                  <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${insurance.color} flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform`}>
                    <insurance.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors">
                      {insurance.title}
                    </h3>
                    <p className="text-gray-400 mb-4">{insurance.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {insurance.features.map((feature) => (
                        <span key={feature} className="px-3 py-1 bg-white/5 text-gray-300 text-sm rounded-full">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                <button className="w-full mt-6 py-3 bg-white/10 text-white font-medium rounded-lg hover:bg-yellow-400 hover:text-black transition-all">
                  Explore Plans →
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Why Buy Insurance?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Insurance is not an expense, it's an investment in your family's security
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-start gap-4 p-6 bg-black/50 rounded-xl border border-zinc-800"
              >
                <div className="w-12 h-12 rounded-lg bg-purple-400/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-1">{benefit.title}</h3>
                  <p className="text-gray-400 text-sm">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-gray-400">Get insured in 4 simple steps</p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6">
            {process.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative text-center"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4 text-black text-2xl font-bold">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
                {index < process.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-yellow-400/50 to-transparent" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="py-20 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Our Insurance Partners
            </h2>
            <p className="text-gray-400">We work with India's most trusted insurers</p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-4">
            {partners.map((partner, index) => (
              <motion.div
                key={partner}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="px-6 py-4 bg-zinc-800/50 border border-zinc-700 rounded-xl text-gray-300 font-medium hover:border-yellow-400/50 hover:text-yellow-400 transition-all cursor-pointer"
              >
                {partner}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-purple-500/20 to-pink-500/10 border border-purple-400/30 rounded-3xl p-12 text-center"
          >
            <Umbrella className="w-16 h-16 text-purple-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Protect Your Tomorrow, Today
            </h2>
            <p className="text-gray-300 mb-8 max-w-xl mx-auto">
              Don't wait for the unexpected. Get comprehensive insurance coverage 
              and secure your family's future now.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-yellow-400 text-black font-bold rounded-xl hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25"
              >
                Get Free Quote <ArrowRight className="inline ml-2 w-5 h-5" />
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 bg-white/10 text-white font-bold rounded-xl border border-white/20 hover:bg-white/20 transition-all"
              >
                Compare Plans
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default InsurancePage;
